import { Avatar, Button, Tooltip } from "@deligoo/ui"
import { memo, useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Popup } from "react-leaflet"
import { batch, shallowEqual, useDispatch } from "react-redux"

import { useAppSelector } from "@/App"
import { DriverOverview } from "@/components/DriverOverview"
import { selectDriverById } from "@/store/drivers"
import { selectCurrentOrderIdOfDriver } from "@/store/orders"
import { setAboutPanel, setFocusedDriver, setFocusedOrderArea, setShowDriverDetails } from "@/store/ui"
import { Driver } from "@/types"
import { DriverUtils } from "@/utils"

import cls from "./Popup.module.scss"

export const DriverPopup = memo(({ driverId }: { driverId: Driver["id"] }) => {
  const popupRef = useRef<Popup | null>(null)

  const driver = useAppSelector(selectDriverById(driverId), shallowEqual)
  const currentOrderId = useAppSelector(selectCurrentOrderIdOfDriver(driverId))

  const dispatch = useDispatch()

  const { t } = useTranslation()

  const showAboutOrder = useCallback(() => {
    if (!currentOrderId) return null

    popupRef.current?.leafletElement["_map"].closePopup()

    batch(() => {
      dispatch(setAboutPanel({ type: "order", id: currentOrderId }))
      dispatch(setShowDriverDetails(driverId))
    })
  }, [dispatch, currentOrderId, driverId])

  if (!driver) return null

  return (
    <Popup
      ref={popupRef}
      closeButton={false}
      closeOnEscapeKey={false} // Overwritten with custom event
      onClose={() => {
        batch(() => {
          dispatch(setFocusedDriver(null))
          dispatch(setFocusedOrderArea(null))
        })
      }}
      minWidth={220}
      maxWidth={220}
    >
      <div className={cls.header}>
        <Tooltip content={t("call")}>
          <span>
            <Button
              as="a"
              href={`tel:${driver.phone}`}
              color="primary"
              size="small"
              variation="outlined"
              iconProps={{ name: "phone", color: "primary" }}
            />
          </span>
        </Tooltip>

        <Avatar
          color={DriverUtils.getAvatarColor(driver)}
          indicator={DriverUtils.getAvatarIndicator(driver)}
          dotColor={DriverUtils.getAvatarDotColor(driver)}
          style={{ margin: "0 1rem" }}
        >
          {driver.name}
        </Avatar>

        <Tooltip content="Telegram">
          <span>
            <Button
              as="a"
              target="_blank"
              rel="noreferrer noopener"
              href={driver.telegram_chat_link || undefined}
              disabled={!driver.telegram_chat_link}
              color="primary"
              size="small"
              variation="outlined"
              iconProps={{ name: "telegram" }}
            />
          </span>
        </Tooltip>
      </div>

      <div className={cls.content}>
        <div className={cls.title}>{driver.name}</div>

        <div className={cls.overview}>
          <DriverOverview driverId={driverId} size="medium" />
        </div>
      </div>

      <Button color="primary" iconProps={{ name: "route" }} disabled={!currentOrderId} onClick={showAboutOrder} raised>
        {t("show_current_route")}
      </Button>
    </Popup>
  )
})
